import { CHARACTER_BY_ID, CONTENT } from "./content";
import { playerColorForSlot } from "./playerNameColor";
import { normalizeCharacterId, normalizeDisplayName } from "./protocol";
import type { DifficultyTier, LobbyPlayer, LobbyState } from "./types";

export const DISPLAY_NAME_TAKEN_MESSAGE =
  "That name is already taken in this room. Choose a different name.";

export function nextFreeLobbySlot(players: readonly LobbyPlayer[], maxPlayers = CONTENT.tuning.maxPlayers): number {
  const used = new Set(players.map((player) => player.slot));
  for (let slot = 0; slot < maxPlayers; slot += 1) {
    if (!used.has(slot)) return slot;
  }
  throw new Error(`Room is full (${maxPlayers} players).`);
}

export function lobbyLabel(slot: number): string {
  return `P${slot + 1}`;
}

export class TeamLobby {
  private readonly players = new Map<string, LobbyPlayer>();

  constructor(players: readonly LobbyPlayer[] = []) {
    for (const player of players) {
      const copy = structuredClone(player);
      copy.inMatch = Boolean(copy.inMatch);
      copy.ready = Boolean(copy.ready);
      const requestedName = normalizeDisplayName(copy.displayName);
      copy.displayName =
        requestedName && this.isDisplayNameAvailable(requestedName)
          ? requestedName
          : this.availableSlotLabel(copy.slot);
      this.players.set(player.id, copy);
    }
  }

  list(): LobbyPlayer[] {
    return [...this.players.values()].sort((first, second) => first.slot - second.slot || first.id.localeCompare(second.id));
  }

  size(): number {
    return this.players.size;
  }

  get(playerId: string): LobbyPlayer | undefined {
    return this.players.get(playerId);
  }

  isDisplayNameAvailable(displayName: string, exceptPlayerId?: string): boolean {
    const normalized = normalizeDisplayName(displayName);
    if (!normalized) return true;
    return !this.list().some(
      (player) => player.id !== exceptPlayerId && normalizeDisplayName(player.displayName) === normalized
    );
  }

  join(playerId: string, characterId: string, displayName?: string): LobbyPlayer {
    const existing = this.players.get(playerId);
    if (existing) return existing;
    if (this.players.size >= CONTENT.tuning.maxPlayers) {
      throw new Error(`Room is full (${CONTENT.tuning.maxPlayers} players).`);
    }

    const slot = nextFreeLobbySlot(this.list());
    const definition = CHARACTER_BY_ID.get(normalizeCharacterId(characterId)) ?? CONTENT.characters[0];
    if (!definition) throw new Error("No playable characters are configured.");
    const requestedName = normalizeDisplayName(displayName);
    if (requestedName && !this.isDisplayNameAvailable(requestedName)) {
      throw new Error(DISPLAY_NAME_TAKEN_MESSAGE);
    }

    const player: LobbyPlayer = {
      id: playerId,
      slot,
      characterId: definition.id,
      displayName: requestedName || this.availableSlotLabel(slot),
      color: playerColorForSlot(slot),
      ready: false,
      inMatch: false
    };
    this.players.set(playerId, player);
    return player;
  }

  leave(playerId: string): boolean {
    return this.players.delete(playerId);
  }

  select(playerId: string, characterId: string): boolean {
    const player = this.players.get(playerId);
    const definition = CHARACTER_BY_ID.get(normalizeCharacterId(characterId));
    if (!player || !definition) return false;
    player.characterId = definition.id;
    player.ready = false;
    return true;
  }

  rename(playerId: string, displayName?: string | null): boolean {
    const player = this.players.get(playerId);
    if (!player) return false;
    const requestedName = normalizeDisplayName(displayName);
    if (requestedName && !this.isDisplayNameAvailable(requestedName, playerId)) {
      throw new Error(DISPLAY_NAME_TAKEN_MESSAGE);
    }
    player.displayName = requestedName || this.availableSlotLabel(player.slot, playerId);
    return true;
  }

  private availableSlotLabel(slot: number, exceptPlayerId?: string): string {
    const preferred = lobbyLabel(slot);
    if (this.isDisplayNameAvailable(preferred, exceptPlayerId)) return preferred;
    let suffix = 2;
    while (!this.isDisplayNameAvailable(`${preferred}-${suffix}`, exceptPlayerId)) suffix += 1;
    return `${preferred}-${suffix}`;
  }

  setReady(playerId: string, ready: boolean): boolean {
    const player = this.players.get(playerId);
    if (!player) return false;
    player.ready = ready;
    return true;
  }

  setInMatch(playerId: string, inMatch: boolean): boolean {
    const player = this.players.get(playerId);
    if (!player) return false;
    player.inMatch = inMatch;
    if (inMatch) player.ready = false;
    return true;
  }

  returnToLobby(playerId: string): boolean {
    const player = this.players.get(playerId);
    if (!player) return false;
    player.inMatch = false;
    player.ready = false;
    return true;
  }

  resetMatchFlags(): void {
    for (const player of this.players.values()) {
      player.inMatch = false;
      player.ready = false;
    }
  }

  anyoneInMatch(): boolean {
    return this.list().some((player) => player.inMatch);
  }

  allReady(): boolean {
    const players = this.list();
    return players.length > 0 && players.every((player) => player.ready && !player.inMatch);
  }

  hostId(): string {
    return this.list()[0]?.id ?? "";
  }

  unreadyAll(): void {
    for (const player of this.players.values()) player.ready = false;
  }

  snapshot(roomCode: string, difficulty: DifficultyTier = "medium"): LobbyState {
    return {
      roomCode,
      difficulty,
      hostId: this.hostId(),
      players: this.list().map((player) => structuredClone(player))
    };
  }
}
